import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertTriangle, RefreshCw, Loader2, AlertCircle, CheckCircle2, Trash2, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SystemPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [wiping, setWiping] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: resp } = await axios.get(`${API}/admin/system/data-counts`, { headers, timeout: 12000 });
      setData(resp);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const required = data?.required_confirmation || 'WIPE_ALL_TEST_DATA';

  const handleWipe = async () => {
    setWiping(true);
    setError('');
    try {
      const { data: resp } = await axios.post(
        `${API}/admin/system/wipe-test-data`,
        { confirmation_code: typed },
        { headers, timeout: 30000 },
      );
      setResult(resp);
      setConfirmOpen(false);
      setTyped('');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="system-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" /> Système — Maintenance
          </h1>
          <p className="text-sm text-gray-500">Outils destructifs réservés à l'administrateur principal.</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="btn-system-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualiser
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <div className="font-semibold mb-1">Purge effectuée — {result.total_deleted} documents supprimés</div>
            <div className="text-xs">Par {result.performed_by} le {new Date(result.performed_at).toLocaleString('fr-CH')}</div>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs">Détail par collection</summary>
              <ul className="text-xs mt-1 pl-4 list-disc">
                {Object.entries(result.deleted || {}).map(([k, v]) => (
                  <li key={k}>{k} : {v} supprimés</li>
                ))}
              </ul>
            </details>
          </AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Chargement…</div>
      ) : data ? (
        <>
          {/* Zone dangereuse */}
          <Card className="border-2 border-red-300 bg-red-50/30">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <Trash2 className="w-6 h-6 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-red-800">Réinitialisation complète des données clients</h2>
                  <p className="text-sm text-red-700 mt-1">
                    Supprime <strong>{data.purgeable_total} documents</strong> répartis dans {Object.keys(data.purgeable).length} collections.
                    Action <strong>IRRÉVERSIBLE</strong>. Réservée à un environnement de pré-production.
                  </p>
                  <p className="text-xs text-red-600 mt-2">
                    ⚠️ Stripe n'est PAS impacté. Si des abonnements Live tournent, annule-les manuellement dans le dashboard Stripe avant.
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-red-200">
                <div>
                  <div className="text-xs uppercase tracking-wider text-red-700 font-semibold mb-2">Collections à purger</div>
                  <ul className="text-sm space-y-1">
                    {Object.entries(data.purgeable).map(([k, v]) => (
                      <li key={k} className="flex justify-between bg-white/70 rounded px-2 py-1">
                        <span className="font-mono text-xs">{k}</span>
                        <span className={v > 0 ? 'text-red-700 font-bold' : 'text-gray-400'}>{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-green-700 font-semibold mb-2 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Collections protégées (jamais touchées)
                  </div>
                  <ul className="text-sm space-y-1">
                    {Object.entries(data.protected).map(([k, v]) => (
                      <li key={k} className="flex justify-between bg-white/70 rounded px-2 py-1">
                        <span className="font-mono text-xs">{k}</span>
                        <span className="text-gray-700">{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <Button
                onClick={() => { setConfirmOpen(true); setTyped(''); }}
                className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
                disabled={data.purgeable_total === 0}
                data-testid="btn-open-wipe"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {data.purgeable_total === 0
                  ? 'Aucune donnée à supprimer'
                  : `Tout effacer (${data.purgeable_total} documents)`}
              </Button>
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !o && !wiping && setConfirmOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Confirmation requise
            </DialogTitle>
            <DialogDescription>
              Cette action va supprimer <strong>définitivement</strong> {data?.purgeable_total} documents.
              Tape exactement <code className="bg-gray-100 px-2 py-0.5 rounded text-red-700 font-bold">{required}</code> pour confirmer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="confirm">Code de confirmation</Label>
              <Input
                id="confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={required}
                className="font-mono"
                autoComplete="off"
                autoFocus
                data-testid="input-wipe-confirm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={wiping}>Annuler</Button>
            <Button
              onClick={handleWipe}
              disabled={typed !== required || wiping}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-wipe"
            >
              {wiping ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Purge en cours…</> : <><Trash2 className="w-4 h-4 mr-2" /> Confirmer la suppression</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemPage;
